import type { MutableRefObject } from 'react';
import { MEITRE_GA_ID } from '@core/config/constants';
import { ANALYTICS_ENV, type AnalyticsEnvironment } from './getEnvironment';
import type { AnalyticsEvent, PageViewEvent } from './types';

/**
 * Configuration for the shared analytics trackers factory.
 *
 * Both AnalyticsProvider (booking) and GroupAnalyticsProvider (group landing)
 * share an identical `trackPageView`/`trackEvent` shape with the same
 * `LOCAL`/`PRODUCTION` env-gating and the same defensive `safeCall` wrapper.
 * The only differences are the `client` tag and whether a per-restaurant GA id
 * receives a second `page_view`. Those differences are parameterized here.
 */
export interface TrackerConfig {
  /**
   * Holds the resolved analytics environment. Providers capture this once via
   * `useRef(getAnalyticsEnvironment())`; the factory reads `.current` on every
   * call to preserve identical lazy-read semantics.
   */
  envRef: MutableRefObject<AnalyticsEnvironment>;
  /** Client tag stamped on every Meitre GA event: 'v2' (booking) | 'v2-group'. */
  clientTag: string;
  /**
   * Optional per-restaurant GA measurement id. When present AND set
   * (`.current` truthy), `trackPageView` ALSO emits a second `page_view` to it,
   * exactly as the booking provider does today. Omit it (group provider) for a
   * single send to the Meitre GA only.
   */
  secondaryGaIdRef?: MutableRefObject<string>;
}

export interface AnalyticsTrackers {
  trackPageView: (pageView: PageViewEvent) => void;
  trackEvent: (event: AnalyticsEvent) => void;
}

/**
 * Build the shared `{ trackPageView, trackEvent }` pair for an analytics
 * provider. Behavior is byte-identical to the inline implementations the two
 * providers carried before this extraction — the only knobs are `clientTag`
 * and the optional `secondaryGaIdRef` double-send.
 */
export function createAnalyticsTrackers(config: TrackerConfig): AnalyticsTrackers {
  const { envRef, clientTag, secondaryGaIdRef } = config;

  // Analytics calls into window.gtag / window.fbq are defensively wrapped:
  // third-party shims (content blockers, defective GTM containers, Sentry
  // overrides) can throw synchronously from gtag. Without this wrapper, a
  // call inside a try/catch (e.g. useReservationCommit's error handler)
  // would abort the catch and suppress the user-visible error message.
  // Analytics is observational — its failure must never break the UI.
  const safeCall = (fn: () => void, context: string): void => {
    try {
      fn();
    } catch (err) {
      // Don't escalate to Sentry from inside an analytics helper — the
      // caller may already be in an error path. Console.warn is sufficient
      // for DEV diagnosis; in PROD this just becomes a no-op.
      // eslint-disable-next-line no-console
      console.warn(`[Analytics] ${context} threw:`, err);
    }
  };

  const trackPageView = (pageView: PageViewEvent): void => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.LOCAL) {
      console.log('[Analytics] PageView:', pageView.path, pageView.title ?? '');
      return;
    }

    if (env === ANALYTICS_ENV.PRODUCTION && window.gtag) {
      safeCall(() => {
        window.gtag!('event', 'page_view', {
          page_path: pageView.path,
          page_title: pageView.title,
          send_to: MEITRE_GA_ID,
          client: clientTag,
        });

        const restaurantGaId = secondaryGaIdRef?.current;
        if (restaurantGaId) {
          window.gtag!('event', 'page_view', {
            page_path: pageView.path,
            page_title: pageView.title,
            send_to: restaurantGaId,
          });
        }
      }, 'trackPageView');
    }
  };

  const trackEvent = (event: AnalyticsEvent): void => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.LOCAL) {
      console.log('[Analytics] Event:', event.name, event.properties ?? {});
      return;
    }

    if (env === ANALYTICS_ENV.PRODUCTION && window.gtag) {
      safeCall(() => {
        window.gtag!('event', event.name, {
          ...event.properties,
          send_to: MEITRE_GA_ID,
          client: clientTag,
        });
      }, `trackEvent(${event.name})`);
    }
  };

  return { trackPageView, trackEvent };
}
